import { Component, Input, ViewChild, OnInit, forwardRef, Injector, ChangeDetectorRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, NgControl } from '@angular/forms';
import { EditorComponent } from 'ngx-monaco-editor-v2';

// Monaco is provided globally once the editor script loads.
declare const monaco: any;

@Component({
    selector: 'sql-editor',
    templateUrl: './sql-editor.component.html',
    styleUrls: ['./sql-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SqlEditorComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class SqlEditorComponent implements ControlValueAccessor, OnInit {
  private static instanceCounter = 0;
  
  @Input() private _value: string = '';
  public ngControl: NgControl | null;
  @Input() readOnly: boolean = false;

  private instanceId: number;

  private editor: any;

  get value(): any {
    return this._value;
  }

  set value(value: any) {
    if (value !== this._value) {
      this._value = value;
      this.onChange(value);
      this.onTouched();
    }
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  @ViewChild(EditorComponent, { static: false })
  monacoComponent: EditorComponent;
  
  // Editor options (the readOnly flag here is just an initial value)
  editorOptions: any = {
    theme: 'appTheme',
    language: 'sql',
    roundedSelection: true,
    automaticLayout: true,
    autoIndent: 'full',
    readOnly: this.readOnly,
  };

  modelUri: any;

  constructor(
    private readonly injector: Injector,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.instanceId = SqlEditorComponent.instanceCounter++;
    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl) {
      setTimeout(() => {
        this.ngControl.control!.updateValueAndValidity();
      });
    }
    this.cdRef.detectChanges();
  }

  editorInit(editor: any) {
    this.editor = editor;
    const model = editor.getModel();
    if (model) {
      model.setValue(this.value || '');
    }
    monaco.editor.setTheme('appTheme');

    editor.updateOptions({ readOnly: this.readOnly });

    editor.onDidChangeModelContent(() => {
      setTimeout(() => this.onEditorValueChange(editor.getValue()));
    });

    if (this.value) {
      editor.setValue(this.value);
    }
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.readOnly ||= isDisabled;

    // If your editor instance is already available, update its options.
    if (this.editor) {
      this.editor.updateOptions({ readOnly: isDisabled });
    }
  }

  onEditorValueChange(value: string): void {
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(value, { emitEvent: false });
      if (this.ngControl.control.updateValueAndValidity) {
        this.ngControl.control.updateValueAndValidity();
      }
    }
  }
}
